<template>
  <RfInputWrapper
    ref="wrapperRef"
    :title="title"
    :requiredTitle="requiredTitle"
    :labelFor="`input${uid}`"
    :variant="variant"
    :size="size"
    :autoWidth="autoWidth"
    :errorMessage="errorMessage"
    :hintMessage="hintMessage"
    :clearable="clearable"
    :hasValue="!!input"
    wrapperClass="gap-2"
    :class="attrsFiltered.class"
    @clear="clearInput"
  >
    <template v-if="slots.title" #title>
      <slot name="title" />
    </template>

    <span v-if="slots.prepend" class="rf-input--prepend inline-flex items-center justify-center">
      <slot name="prepend"></slot>
    </span>
    <div
      class="rf-input--input-wrapper relative flex w-full min-w-px grow self-stretch"
      :class="{ 'pl-3': !slots.prepend }"
    >
      <label
        :for="`input${uid}`"
        aria-hidden="true"
        class="absolute inset-0 -z-10 cursor-text"
      ></label>
      <input
        :id="`input${uid}`"
        ref="inputEl"
        v-bind="attrsFiltered.rest"
        v-model="input"
        :disabled="isDisabled"
        :placeholder="placeholder"
        :aria-invalid="!!errorMessage"
        :aria-describedby="wrapperRef?.messageId || undefined"
        class="rf-input--input text-body placeholder:text-beige-50 w-full grow bg-transparent pr-4 text-ellipsis focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-50"
        :class="{ 'text-beige-30': isDisabled }"
        @blur="emit('blur', $event)"
      />
    </div>
    <span
      v-if="slots.append || loading"
      class="rf-input--append inline-flex items-center justify-center"
    >
      <RfProgressActivityIcon
        v-if="loading"
        class="rf-input--loader fill-beige-30 h-5 w-5 animate-spin"
      />
      <slot v-else name="append"></slot>
    </span>
  </RfInputWrapper>
</template>

<script setup>
import { computed, ref, useAttrs, getCurrentInstance, useSlots } from "vue";
import RfProgressActivityIcon from "@/components/icons/RfProgressActivityIcon.vue";
import RfInputWrapper from "@/components/inputs/RfInputWrapper.vue";

defineOptions({ inheritAttrs: false });

const { uid } = getCurrentInstance();
const slots = useSlots();
const wrapperRef = ref();
const input = defineModel("modelValue", { type: [String, Number], default: "" });
const emit = defineEmits(["blur"]);
const inputEl = ref();

const props = defineProps({
  placeholder: { type: String, default: "Enter value" },
  title: { type: String, default: "" },
  size: { type: String, default: "base" },
  variant: { type: String, default: "default" },
  errorMessage: { type: String, default: "" },
  hintMessage: { type: String, default: "" },
  requiredTitle: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  autoWidth: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  clearable: { type: Boolean, default: false },
});

const clearInput = () => {
  input.value = "";
  inputEl.value?.focus();
};

const attrs = useAttrs();
const attrsFiltered = computed(() => {
  const { class: cssClass, ...rest } = attrs;
  return { rest, class: cssClass };
});

const isDisabled = computed(() => props.disabled || props.loading);
</script>

<style lang="scss" scoped>
.rf-input {
  &.rf-input--base {
    :deep(.rf-input--prepend) {
      margin-left: 11px;
    }
    :deep(.rf-input--append) {
      margin-right: 11px;
    }
  }
  &.rf-input--lg {
    :deep(.rf-input--prepend) {
      margin-left: 12px;
    }
    :deep(.rf-input--append) {
      margin-right: 12px;
    }
  }
}
</style>
